import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

interface TruncationConfig {
  mobile: number;
  desktop: number;
  suffix?: string;
}

interface GreetingTruncationConfig extends TruncationConfig {
  greetingMessageId: string;
}

/**
 * Hook for responsive text truncation with memoized calculations
 */
export const useResponsiveTruncation = (
  config: TruncationConfig
): ((text: string, isMobile: boolean) => string) => {
  const { mobile, desktop, suffix = '...' } = config;

  const getTruncatedText = useCallback(
    (text: string, isMobile: boolean): string => {
      const maxLength = isMobile ? mobile : desktop;

      if (text.length <= maxLength) {
        return text;
      }

      const truncatedLength = Math.max(1, maxLength - suffix.length);
      return `${text.substring(0, truncatedLength)}${suffix}`;
    },
    [mobile, desktop, suffix]
  );

  return getTruncatedText;
};

/**
 * Hook for truncating greeting messages with dynamic prefix calculation
 */
export const useGreetingTruncation = (
  config: GreetingTruncationConfig
): ((name: string, isMobile: boolean) => string) => {
  const intl = useIntl();
  const { greetingMessageId, mobile, desktop, suffix = '...' } = config;

  // Memoize the greeting prefix calculation
  const greetingPrefix = useMemo(() => {
    return intl.formatMessage({ id: greetingMessageId }, { firstName: '' });
  }, [intl, greetingMessageId]);

  const getTruncatedName = useCallback(
    (name: string, isMobile: boolean): string => {
      const maxGreetingLength = isMobile ? mobile : desktop;
      const availableLength = maxGreetingLength - greetingPrefix.length;

      if (name.length <= availableLength) {
        return name;
      }

      const truncatedLength = Math.max(1, availableLength - suffix.length);
      return `${name.substring(0, truncatedLength)}${suffix}`;
    },
    [greetingPrefix, mobile, desktop, suffix]
  );

  return getTruncatedName;
};
